/**
 * My Collaborations Page
 * Professional dashboard for managing lead collaborations
 * Features tabbed views, stats overview, invitation badge, and inline actions
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from 'primereact/avatargroup';
import { ProgressBar } from 'primereact/progressbar';
import { Skeleton } from 'primereact/skeleton';
import { Badge } from 'primereact/badge';
import { Tooltip } from 'primereact/tooltip';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Paginator } from 'primereact/paginator';
import { SelectButton } from 'primereact/selectbutton';
import { Calendar } from 'primereact/calendar';

import {
    collaborationService,
    getStatusConfig,
    getRoleConfig,
    getCommissionModelConfig,
    getContributionLevelConfig,
    formatCommission,
    formatCurrency,
    formatLargeAmount,
    formatTimeAgo,
    getMilestoneStatus,
    COLLABORATION_STATUS,
    COMMISSION_MODELS
} from '../../services/collaborationService';

import { formatLoanAmount, getLoanProductConfig, PRODUCT_TYPE_DETAILED_OPTIONS } from '../../services/marketplaceService';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';

// Lead Deals section tabs
const LEAD_DEALS_TABS = [
    { label: 'My Deals', icon: 'pi pi-briefcase', route: '/app/collaborations' },
    { label: 'Lead Conversions', icon: 'pi pi-check-circle', route: '/app/collaborations/conversions' }
];

// Outline style for Tags - border only, no solid fill
const outlineTagStyle = (severity) => {
    const colors = {
        warning: { background: 'transparent', border: '1.5px solid #EAB308', color: '#EAB308' },
        info: { background: 'transparent', border: '1.5px solid #3B82F6', color: '#3B82F6' },
        success: { background: 'transparent', border: '1.5px solid #22C55E', color: '#22C55E' },
        danger: { background: 'transparent', border: '1.5px solid #EF4444', color: '#EF4444' },
        secondary: { background: 'transparent', border: '1.5px solid #94A3B8', color: '#64748B' },
        help: { background: 'transparent', border: '1.5px solid #A855F7', color: '#A855F7' },
    };
    return colors[severity] || colors.secondary;
};

const CollaborationsList = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [collaborations, setCollaborations] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        completed: 0,
        cancelled: 0,
        disputed: 0,
        as_owner: 0,
        as_collaborator: 0,
        total_earnings: 0
    });
    const [activeTab, setActiveTab] = useState(0);
    const [globalFilter, setGlobalFilter] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const searchTimeoutRef = useRef(null);

    // Pagination state (server-side)
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const ROWS_PER_PAGE = 10;

    // Filters
    const [filters, setFilters] = useState({
        status: null,
        role: null,
        commission_model: null
    });
    const [loanTypeFilter, setLoanTypeFilter] = useState(null);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);

    // View mode state
    const [viewMode, setViewMode] = useState('grid');
    const viewOptions = [
        { icon: 'pi pi-th-large', value: 'grid' },
        { icon: 'pi pi-list', value: 'list' }
    ];

    // Debounce search query
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            // Reset pagination when search term changes
            setFirst(0);
            setPage(1);
            setDebouncedSearchQuery(globalFilter);
        }, 500);
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [globalFilter]);

    // Tab configuration
    const tabConfig = [
        { key: 'all', label: 'All', icon: 'pi-list', statsKey: 'total' },
        { key: 'active', label: 'Active', icon: 'pi-play-circle', statsKey: 'active', color: 'success' },
        { key: 'as_owner', label: 'As Owner', icon: 'pi-crown', statsKey: 'as_owner', color: 'help' },
        { key: 'as_collaborator', label: 'As Collaborator', icon: 'pi-users', statsKey: 'as_collaborator', color: 'info' },
        { key: 'completed', label: 'Completed', icon: 'pi-check-circle', statsKey: 'completed', color: 'info' },
        { key: 'disputed', label: 'Disputed', icon: 'pi-exclamation-triangle', statsKey: 'disputed', color: 'warning' }
    ];

    // Helper to get lead data from collaboration
    const getLeadData = (collab) => {
        return collab.lead_snapshot || collab.lead || collab.marketplace_lead?.lead_snapshot || {};
    };

    // Helper to get borrower name
    const getBorrowerName = (collab) => {
        const lead = getLeadData(collab);
        if (lead.borrower_name) return lead.borrower_name;
        if (lead.masked_name) return lead.masked_name;
        if (lead.full_name) return lead.full_name;
        return 'Unknown Borrower';
    };

    // Helper to get loan amount
    const getLoanAmount = (collab) => {
        const lead = getLeadData(collab);
        return lead.loan_amount_required || lead.loan_amount || lead.amount || 0;
    };

    // Helper to get product type
    const getProductType = (collab) => {
        const lead = getLeadData(collab);
        return lead.product_type_detailed || lead.product_type || 'personal_loan';
    };

    // Fetch collaborations with server-side pagination
    const fetchCollaborations = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: ROWS_PER_PAGE
            };

            // Apply tab-based filters
            const currentTab = tabConfig[activeTab];
            if (currentTab.key === 'active') {
                params.status = 'active';
            } else if (currentTab.key === 'completed') {
                params.status = 'completed';
            } else if (currentTab.key === 'disputed') {
                params.status = 'disputed';
            } else if (currentTab.key === 'as_owner') {
                params.role = 'owner';
            } else if (currentTab.key === 'as_collaborator') {
                params.role = 'collaborator';
            }

            // Apply commission_model filter if set
            if (filters.commission_model) {
                params.commission_model = filters.commission_model;
            }

            // Add search term if present
            if (debouncedSearchQuery) {
                params.search = debouncedSearchQuery;
            }

            // Loan type filter
            if (loanTypeFilter) {
                params.loan_type = loanTypeFilter;
            }

            // Date range filters
            if (dateFrom) {
                const d = new Date(dateFrom);
                params.date_from = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            }
            if (dateTo) {
                const d = new Date(dateTo);
                params.date_to = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            }

            const response = await collaborationService.getMyCollaborations(params);

            if (response.success) {
                let collabData = [];
                const data = response.data;

                // Handle Laravel pagination response
                if (data) {
                    if (data.data && Array.isArray(data.data)) {
                        collabData = data.data;
                        setTotalRecords(data.total || data.data.length);
                    } else if (Array.isArray(data)) {
                        collabData = data;
                        setTotalRecords(data.length);
                    } else if (data.collaborations && Array.isArray(data.collaborations)) {
                        collabData = data.collaborations;
                        setTotalRecords(data.total || data.collaborations.length);
                    }
                }

                setCollaborations(collabData);

                // Use stats from backend response (always includes all status counts)
                if (response.data?.stats || response.stats) {
                    setStats(prev => ({ ...prev, ...(response.data?.stats || response.stats) }));
                }
            }
        } catch (error) {
            console.error('Failed to fetch collaborations:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load lead deals'
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when page, tab, search, or filter changes
    useEffect(() => {
        fetchCollaborations();
    }, [activeTab, page, debouncedSearchQuery, filters.commission_model, loanTypeFilter, dateFrom, dateTo]);

    // Handle tab change with pagination reset
    const handleTabChange = (index) => {
        setFirst(0);
        setPage(1);
        setActiveTab(index);
    };

    // Handle page change
    const onPageChange = (e) => {
        setFirst(e.first);
        setPage(Math.floor(e.first / ROWS_PER_PAGE) + 1);
    };

    // Handle leave collaboration
    const handleLeave = (collab) => {
        confirmDialog({
            message: 'Are you sure you want to leave this lead deal? You will forfeit your commission.',
            header: 'Leave Lead Deal',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const response = await collaborationService.leaveCollaboration(collab.id);
                    if (response.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Left',
                            detail: 'You have left the lead deal'
                        });
                        fetchCollaborations();
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to leave lead deal'
                    });
                }
            }
        });
    };

    // Column Templates
    const leadInfoTemplate = (rowData) => {
        const name = getBorrowerName(rowData);
        const amount = getLoanAmount(rowData);
        const productType = getProductType(rowData);
        const config = getLoanProductConfig(productType);

        return (
            <div className="flex align-items-center gap-3">
                <div
                    className="flex align-items-center justify-content-center border-round"
                    style={{
                        width: '44px',
                        height: '44px',
                        backgroundColor: '#eff6ff'
                    }}
                >
                    <i className={`pi ${config.icon} text-primary text-xl`} />
                </div>
                <div>
                    <div className="font-semibold text-900 mb-1">{name}</div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-green-600 font-bold">{formatLargeAmount(amount)}</span>
                        <Tag value={config.label} className="text-xs" style={{ ...outlineTagStyle('info') }} />
                    </div>
                </div>
            </div>
        );
    };

    const myRoleTemplate = (rowData) => {
        const role = rowData.my_role || 'collaborator';
        const config = getRoleConfig(role);

        return (
            <Tag
                value={config.label}
                icon={`pi ${config.icon}`}
                style={{
                    backgroundColor: config.bgColor,
                    color: config.color,
                    border: 'none'
                }}
                className="text-xs font-medium"
            />
        );
    };

    const commissionModelTemplate = (rowData) => {
        const model = rowData.commission_model;
        const config = getCommissionModelConfig(model);

        return (
            <div className="flex align-items-center gap-2">
                <i className={`pi ${config.icon} text-500`} />
                <span className="text-600 text-sm">{config.label}</span>
            </div>
        );
    };

    const myCommissionTemplate = (rowData) => {
        const model = rowData.commission_model;
        const myCommission = rowData.my_commission_percentage || rowData.my_commission_amount;
        const estimatedAmount = rowData.my_estimated_commission || 0;

        return (
            <div className="text-center">
                <div className="text-green-600 font-bold text-lg">
                    {model === 'percentage_split'
                        ? `${myCommission || 0}%`
                        : formatCurrency(myCommission || estimatedAmount || 0)
                    }
                </div>
                {model === 'percentage_split' && estimatedAmount > 0 && (
                    <div className="text-400 text-xs">~{formatCurrency(estimatedAmount)}</div>
                )}
            </div>
        );
    };

    const membersTemplate = (rowData) => {
        const members = rowData.members || [];
        const displayCount = Math.min(members.length, 3);
        const remaining = members.length - displayCount;

        if (members.length === 0) {
            return <span className="text-400 text-sm">No members</span>;
        }

        return (
            <div className="flex align-items-center gap-2">
                <AvatarGroup>
                    {members.slice(0, displayCount).map((member, idx) => (
                        <Avatar
                            key={idx}
                            label={member.name?.charAt(0) || 'U'}
                            size="normal"
                            shape="circle"
                            style={{
                                backgroundColor: getRoleConfig(member.role).bgColor,
                                color: getRoleConfig(member.role).color
                            }}
                            className="text-sm font-medium"
                        />
                    ))}
                    {remaining > 0 && (
                        <Avatar
                            label={`+${remaining}`}
                            size="normal"
                            shape="circle"
                            className="bg-gray-200 text-gray-600 text-sm"
                        />
                    )}
                </AvatarGroup>
                <span className="text-500 text-sm">{members.length} members</span>
            </div>
        );
    };

    const progressTemplate = (rowData) => {
        if (rowData.commission_model !== 'milestone_based') {
            return <span className="text-400 text-sm">-</span>;
        }

        const milestoneStatus = getMilestoneStatus(rowData.milestones);

        return (
            <div style={{ minWidth: '80px' }}>
                <div className="flex justify-content-between mb-1">
                    <span className="text-600 text-xs">{milestoneStatus.text}</span>
                </div>
                <ProgressBar
                    value={milestoneStatus.percentage}
                    showValue={false}
                    style={{ height: '6px' }}
                    className={milestoneStatus.percentage >= 100 ? 'bg-green-100' : ''}
                 aria-label="Progress" />
            </div>
        );
    };

    const statusTemplate = (rowData) => {
        const config = getStatusConfig(rowData.status);
        return (
            <Tag
                value={config.label}
                icon={`pi ${config.icon}`}
                style={{ ...outlineTagStyle(config.severity) }}
                className="text-xs"
            />
        );
    };

    const dateTemplate = (rowData) => (
        <div className="text-600 text-sm">
            <i className="pi pi-clock text-xs mr-1" />
            {formatTimeAgo(rowData.created_at)}
        </div>
    );

    const actionsTemplate = (rowData) => {
        const isOwner = rowData.my_role === 'owner';
        const isActive = rowData.status === 'active';
        const canLeave = !isOwner && isActive;

        return (
            <div className="flex gap-1">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-text p-button-sm"
                    tooltip="View Details"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => navigate(`/app/collaborations/${rowData.id}`)}
                />
                {isOwner && isActive && (
                    <>
                        <Button
                            icon="pi pi-user-plus"
                            className="p-button-rounded p-button-text p-button-success p-button-sm"
                            tooltip="Invite Member"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/app/collaborations/${rowData.id}?action=invite`)}
                        />
                        <Button
                            icon="pi pi-check-circle"
                            className="p-button-rounded p-button-text p-button-info p-button-sm"
                            tooltip="Complete"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/app/collaborations/${rowData.id}?action=complete`)}
                        />
                    </>
                )}
                {canLeave && (
                    <Button
                        icon="pi pi-sign-out"
                        className="p-button-rounded p-button-text p-button-warning p-button-sm"
                        tooltip="Leave"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleLeave(rowData)}
                    />
                )}
                {isActive && (
                    <Button
                        icon="pi pi-exclamation-triangle"
                        className="p-button-rounded p-button-text p-button-danger p-button-sm"
                        tooltip="Raise Dispute"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => navigate(`/app/collaborations/${rowData.id}?action=dispute`)}
                    />
                )}
            </div>
        );
    };

    // Grid Card Component - Compact design for desktop grid view
    const CollaborationCard = ({ collab }) => {
        const name = getBorrowerName(collab);
        const amount = getLoanAmount(collab);
        const productType = getProductType(collab);
        const productConfig = getLoanProductConfig(productType);
        const statusConfig = getStatusConfig(collab.status);
        const roleConfig = getRoleConfig(collab.my_role);
        const isOwner = collab.my_role === 'owner';
        const isActive = collab.status === 'active';
        const canLeave = !isOwner && isActive;
        const members = collab.members || [];

        // Get commission display
        const getCommissionDisplay = () => {
            if (collab.commission_model === 'percentage_split') {
                return `${collab.my_commission_percentage || 0}%`;
            }
            return formatCurrency(collab.my_commission_amount || collab.my_estimated_commission || 0);
        };

        // Milestone progress
        const milestoneStatus = collab.commission_model === 'milestone_based'
            ? getMilestoneStatus(collab.milestones)
            : null;

        return (
            <div
                className="surface-card border-round border-1 surface-border p-2 cursor-pointer hover:shadow-2 transition-all transition-duration-200 h-full"
                style={{
                    borderLeftWidth: '3px',
                    borderLeftColor: roleConfig?.color || 'var(--primary-500)'
                }}
                onClick={() => navigate(`/app/collaborations/${collab.id}`)}
            >
                {/* Header: Lead + Status */}
                <div className="flex align-items-center justify-content-between mb-1">
                    <div className="flex align-items-center gap-2 flex-1 overflow-hidden">
                        <div
                            className="flex align-items-center justify-content-center border-round flex-shrink-0"
                            style={{ width: '28px', height: '28px', backgroundColor: '#eff6ff' }}
                        >
                            <i className={`pi ${productConfig.icon} text-primary`} style={{ fontSize: '0.75rem' }}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.85rem' }}>
                                {name}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.7rem' }}>
                                {productConfig.label}
                            </div>
                        </div>
                    </div>
                    <Tag
                        value={statusConfig.label}
                        style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.6rem', padding: '2px 6px' }}
                        className="flex-shrink-0"
                    />
                </div>

                {/* Amount */}
                <div className="text-green-600 font-bold mb-2" style={{ fontSize: '0.95rem' }}>
                    {formatLoanAmount(amount)}
                </div>

                {/* Stats Row */}
                <div className="surface-100 border-round p-2 mb-2">
                    <div className="flex justify-content-between text-center">
                        <div className="flex-1">
                            <Tag
                                value={roleConfig?.label || 'Member'}
                                style={{
                                    backgroundColor: roleConfig?.bgColor,
                                    color: roleConfig?.color,
                                    border: 'none',
                                    fontSize: '0.6rem',
                                    padding: '2px 6px'
                                }}
                            />
                            <div className="text-400 mt-1" style={{ fontSize: '0.6rem' }}>Role</div>
                        </div>
                        <div className="flex-1 border-left-1 border-200">
                            <div className="text-green-600 font-bold" style={{ fontSize: '0.8rem' }}>{getCommissionDisplay()}</div>
                            <div className="text-400" style={{ fontSize: '0.6rem' }}>My Share</div>
                        </div>
                        <div className="flex-1 border-left-1 border-200">
                            <div className="text-600 font-semibold" style={{ fontSize: '0.8rem' }}>{members.length}</div>
                            <div className="text-400" style={{ fontSize: '0.6rem' }}>Members</div>
                        </div>
                    </div>
                </div>

                {/* Milestone Progress Bar */}
                {milestoneStatus && (
                    <div className="mb-2">
                        <div className="flex justify-content-between mb-1">
                            <span className="text-500" style={{ fontSize: '0.65rem' }}>Progress</span>
                            <span className="text-600 font-medium" style={{ fontSize: '0.65rem' }}>{milestoneStatus.text}</span>
                        </div>
                        <ProgressBar
                            value={milestoneStatus.percentage}
                            showValue={false}
                            style={{ height: '4px' }}
                         aria-label="Progress" />
                    </div>
                )}

                {/* Team Members */}
                {members.length > 0 && (
                    <div className="flex align-items-center gap-1 mb-2">
                        <AvatarGroup>
                            {members.slice(0, 3).map((member, idx) => (
                                <Avatar
                                    key={idx}
                                    label={member.name?.charAt(0) || 'U'}
                                    shape="circle"
                                    style={{
                                        backgroundColor: getRoleConfig(member.role)?.bgColor || 'var(--primary-100)',
                                        color: getRoleConfig(member.role)?.color || 'var(--primary-600)',
                                        width: '1.5rem',
                                        height: '1.5rem',
                                        fontSize: '0.6rem'
                                    }}
                                />
                            ))}
                            {members.length > 3 && (
                                <Avatar
                                    label={`+${members.length - 3}`}
                                    shape="circle"
                                    className="bg-gray-200 text-gray-600"
                                    style={{ width: '1.5rem', height: '1.5rem', fontSize: '0.55rem' }}
                                />
                            )}
                        </AvatarGroup>
                    </div>
                )}

                {/* Footer: Time */}
                <div className="text-400 mb-2" style={{ fontSize: '0.65rem' }}>
                    <i className="pi pi-clock mr-1" style={{ fontSize: '0.6rem' }}></i>
                    {formatTimeAgo(collab.created_at)}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-1 pt-2 border-top-1 surface-border">
                    <Button
                        icon="pi pi-eye"
                        label="View"
                        className="p-button-text flex-1"
                        style={{ fontSize: '0.7rem', padding: '0.3rem 0.5rem' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/app/collaborations/${collab.id}`);
                        }}
                    />
                    {isOwner && isActive && (
                        <Button
                            icon="pi pi-check-circle"
                            className="p-button-success p-button-text"
                            style={{ fontSize: '0.7rem', padding: '0.3rem' }}
                            tooltip="Complete"
                            tooltipOptions={{ position: 'top' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/app/collaborations/${collab.id}?action=complete`);
                            }}
                        />
                    )}
                    {canLeave && (
                        <Button
                            icon="pi pi-sign-out"
                            className="p-button-warning p-button-text"
                            style={{ fontSize: '0.7rem', padding: '0.3rem' }}
                            tooltip="Leave"
                            tooltipOptions={{ position: 'top' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLeave(collab);
                            }}
                        />
                    )}
                </div>
            </div>
        );
    };

    // List Header Component - Exact same as dashboard tabs
    const ListHeader = () => (
        <div className="hidden md:flex surface-100 border-round px-3 py-2 mb-2 align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
            <div className="flex-1" style={{ minWidth: '180px' }}>
                <span className="text-600 font-semibold">Lead / Product</span>
            </div>
            <div className="hidden lg:block" style={{ width: '100px' }}>
                <span className="text-600 font-semibold">Loan Amount</span>
            </div>
            <div style={{ width: '80px' }}>
                <span className="text-600 font-semibold">My Share</span>
            </div>
            <div className="hidden lg:block" style={{ width: '80px' }}>
                <span className="text-600 font-semibold">Members</span>
            </div>
            <div style={{ width: '70px' }}>
                <span className="text-600 font-semibold">Role</span>
            </div>
            <div style={{ width: '70px' }}>
                <span className="text-600 font-semibold">Status</span>
            </div>
            <div className="hidden lg:block" style={{ width: '80px' }}>
                <span className="text-600 font-semibold">Created</span>
            </div>
            <div style={{ width: '120px' }}>
                <span className="text-600 font-semibold">Actions</span>
            </div>
        </div>
    );

    // List Row Component - Exact same column order as ListHeader and dashboard tabs
    const CollaborationRow = ({ collab }) => {
        const name = getBorrowerName(collab);
        const amount = getLoanAmount(collab);
        const productType = getProductType(collab);
        const productConfig = getLoanProductConfig(productType);
        const statusConfig = getStatusConfig(collab.status);
        const roleConfig = getRoleConfig(collab.my_role);
        const isOwner = collab.my_role === 'owner';
        const isActive = collab.status === 'active';
        const canLeave = !isOwner && isActive;
        const members = collab.members || [];

        // Get commission display
        const getCommissionDisplay = () => {
            if (collab.commission_model === 'percentage_split') {
                return `${collab.my_commission_percentage || 0}%`;
            }
            return formatCurrency(collab.my_commission_amount || collab.my_estimated_commission || 0);
        };

        return (
            <div
                className="surface-card border-round border-1 surface-border px-3 py-2 mb-2 cursor-pointer hover:shadow-2 transition-all transition-duration-150"
                style={{ borderLeftWidth: '3px', borderLeftColor: roleConfig?.color || 'var(--primary-500)' }}
                onClick={() => navigate(`/app/collaborations/${collab.id}`)}
            >
                <div className="flex align-items-center gap-2">
                    {/* Lead / Product */}
                    <div className="flex align-items-center gap-2 flex-1" style={{ minWidth: '180px' }}>
                        <div
                            className="flex align-items-center justify-content-center border-round flex-shrink-0"
                            style={{ width: '32px', height: '32px', backgroundColor: '#eff6ff' }}
                        >
                            <i className={`pi ${productConfig.icon} text-primary`} style={{ fontSize: '0.8rem' }}></i>
                        </div>
                        <div className="min-w-0">
                            <div className="font-semibold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.85rem' }}>
                                {name}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.7rem' }}>
                                {productConfig.label}
                            </div>
                        </div>
                    </div>

                    {/* Loan Amount - hidden on smaller screens */}
                    <div className="hidden lg:block" style={{ width: '100px' }}>
                        <span className="text-green-600 font-bold" style={{ fontSize: '0.85rem' }}>
                            {formatLoanAmount(amount)}
                        </span>
                    </div>

                    {/* My Share */}
                    <div style={{ width: '80px' }}>
                        <span className="text-primary font-bold" style={{ fontSize: '0.85rem' }}>
                            {getCommissionDisplay()}
                        </span>
                    </div>

                    {/* Members - hidden on smaller screens */}
                    <div className="hidden lg:flex align-items-center" style={{ width: '80px' }}>
                        {members.length > 0 ? (
                            <AvatarGroup>
                                {members.slice(0, 2).map((member, idx) => (
                                    <Avatar
                                        key={idx}
                                        label={member.name?.charAt(0) || 'U'}
                                        shape="circle"
                                        style={{
                                            backgroundColor: getRoleConfig(member.role)?.bgColor || 'var(--primary-100)',
                                            color: getRoleConfig(member.role)?.color || 'var(--primary-600)',
                                            width: '1.5rem',
                                            height: '1.5rem',
                                            fontSize: '0.6rem'
                                        }}
                                    />
                                ))}
                                {members.length > 2 && (
                                    <Avatar
                                        label={`+${members.length - 2}`}
                                        shape="circle"
                                        className="bg-gray-200 text-gray-600"
                                        style={{ width: '1.5rem', height: '1.5rem', fontSize: '0.55rem' }}
                                    />
                                )}
                            </AvatarGroup>
                        ) : (
                            <span className="text-400" style={{ fontSize: '0.75rem' }}>-</span>
                        )}
                    </div>

                    {/* Role */}
                    <div style={{ width: '70px' }}>
                        <Tag
                            value={roleConfig?.label || 'Member'}
                            style={{
                                backgroundColor: roleConfig?.bgColor,
                                color: roleConfig?.color,
                                border: 'none',
                                fontSize: '0.65rem',
                                padding: '2px 6px'
                            }}
                        />
                    </div>

                    {/* Status */}
                    <div style={{ width: '70px' }}>
                        <Tag
                            value={statusConfig.label}
                            style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.65rem', padding: '2px 6px' }}
                        />
                    </div>

                    {/* Created - hidden on smaller screens */}
                    <div className="hidden lg:block text-500" style={{ width: '80px', fontSize: '0.75rem' }}>
                        {formatTimeAgo(collab.created_at)}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1" style={{ width: '120px' }}>
                        <Button
                            icon="pi pi-eye"
                            className="p-button-text p-button-sm"
                            style={{ fontSize: '0.7rem', padding: '0.3rem' }}
                            tooltip="View"
                            tooltipOptions={{ position: 'top' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/app/collaborations/${collab.id}`);
                            }}
                        />
                        {canLeave && (
                            <Button
                                icon="pi pi-sign-out"
                                className="p-button-warning p-button-text p-button-sm"
                                style={{ fontSize: '0.7rem', padding: '0.3rem' }}
                                tooltip="Leave"
                                tooltipOptions={{ position: 'top' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleLeave(collab);
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Empty state - Compact
    const emptyTemplate = () => (
        <div className="flex flex-column align-items-center justify-content-center py-4">
            <div className="bg-primary-50 border-circle p-3 mb-2">
                <i className="pi pi-users text-primary" style={{ fontSize: '1.5rem' }} />
            </div>
            <h4 className="text-900 font-semibold m-0 mb-1" style={{ fontSize: '0.9rem' }}>No Lead Deals Found</h4>
            <p className="text-500 text-center mb-2" style={{ maxWidth: '280px', fontSize: '0.75rem' }}>
                Browse the marketplace to find leads seeking collaborators!
            </p>
            <Button
                label="Browse Marketplace"
                icon="pi pi-shopping-cart"
                className="p-button-sm"
                style={{ fontSize: '0.75rem', padding: '0.4rem 0.75rem' }}
                onClick={() => navigate('/app/marketplace')}
            />
        </div>
    );

    // Loading skeleton - supports both grid and list views
    const renderSkeleton = () => {
        if (viewMode === 'grid') {
            return (
                <div className="grid">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="col-12 md:col-6 lg:col-4 xl:col-3 p-2">
                            <div className="surface-card border-round border-1 surface-border p-2" style={{ borderLeftWidth: '3px' }}>
                                <div className="flex align-items-center gap-2 mb-2">
                                    <Skeleton width="28px" height="28px" borderRadius="4px" />
                                    <div className="flex-1">
                                        <Skeleton width="70%" height="0.85rem" className="mb-1" />
                                        <Skeleton width="50%" height="0.7rem" />
                                    </div>
                                    <Skeleton width="50px" height="1rem" />
                                </div>
                                <Skeleton width="45%" height="0.95rem" className="mb-2" />
                                <Skeleton width="100%" height="3rem" className="mb-2" />
                                <div className="flex gap-1 mb-2">
                                    <Skeleton shape="circle" size="1.5rem" />
                                    <Skeleton shape="circle" size="1.5rem" />
                                    <Skeleton shape="circle" size="1.5rem" />
                                </div>
                                <Skeleton width="50%" height="0.65rem" className="mb-2" />
                                <Skeleton width="100%" height="1.75rem" />
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="flex flex-column gap-2">
                {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="surface-card border-round border-1 surface-border px-3 py-2" style={{ borderLeftWidth: '3px' }}>
                        <div className="flex align-items-center gap-3">
                            <div className="flex align-items-center gap-2 flex-1">
                                <Skeleton width="32px" height="32px" borderRadius="4px" />
                                <div>
                                    <Skeleton width="140px" height="0.85rem" className="mb-1" />
                                    <Skeleton width="90px" height="0.7rem" />
                                </div>
                            </div>
                            <Skeleton width="60px" height="1rem" />
                            <Skeleton width="80px" height="0.85rem" />
                            <Skeleton width="70px" height="0.85rem" />
                            <div className="flex gap-1">
                                <Skeleton shape="circle" size="1.5rem" />
                                <Skeleton shape="circle" size="1.5rem" />
                            </div>
                            <Skeleton width="60px" height="1rem" />
                            <Skeleton width="70px" height="0.75rem" />
                            <div className="flex gap-1">
                                <Skeleton width="26px" height="26px" />
                                <Skeleton width="26px" height="26px" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };


    // Mobile Card View for collaborations - Compact
    const renderMobileCard = (collab) => {
        const name = getBorrowerName(collab);
        const amount = getLoanAmount(collab);
        const productType = getProductType(collab);
        const productConfig = getLoanProductConfig(productType);
        const statusConfig = getStatusConfig(collab.status);
        const roleConfig = getRoleConfig(collab.my_role);
        const isOwner = collab.my_role === 'owner';
        const isActive = collab.status === 'active';
        const canLeave = !isOwner && isActive;

        return (
            <div
                key={collab.id}
                className="surface-card border-round shadow-1 p-2 mobile-collab-card"
                style={{ borderLeft: `3px solid ${roleConfig.color || '#3b82f6'}` }}
            >
                {/* Top Row: Lead Name + Status */}
                <div className="flex justify-content-between align-items-start mb-1">
                    <div className="flex align-items-center gap-2 overflow-hidden" style={{ maxWidth: '65%' }}>
                        <div
                            className="flex align-items-center justify-content-center border-round flex-shrink-0"
                            style={{ width: '24px', height: '24px', backgroundColor: '#eff6ff' }}
                        >
                            <i className={`pi ${productConfig.icon} text-primary`} style={{ fontSize: '0.7rem' }} />
                        </div>
                        <div className="overflow-hidden">
                            <div className="font-semibold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.8rem' }}>{name}</div>
                            <div className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatLoanAmount(amount)}</div>
                        </div>
                    </div>
                    <Tag
                        value={statusConfig.label}
                        style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.6rem', padding: '2px 6px' }}
                        className="flex-shrink-0"
                    />
                </div>

                {/* Middle: 2x2 Stats Grid - Compact */}
                <div className="grid mb-1">
                    <div className="col-6 p-1">
                        <div className="surface-50 border-round p-1 text-center">
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>Role</div>
                            <Tag
                                value={roleConfig.label}
                                style={{
                                    backgroundColor: roleConfig.bgColor,
                                    color: roleConfig.color,
                                    border: 'none',
                                    fontSize: '0.6rem',
                                    padding: '1px 4px'
                                }}
                            />
                        </div>
                    </div>
                    <div className="col-6 p-1">
                        <div className="surface-50 border-round p-1 text-center">
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>My Share</div>
                            <div className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>
                                {collab.commission_model === 'percentage_split'
                                    ? `${collab.my_commission_percentage || 0}%`
                                    : formatCurrency(collab.my_commission_amount || 0)
                                }
                            </div>
                        </div>
                    </div>
                    <div className="col-6 p-1">
                        <div className="surface-50 border-round p-1 text-center">
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>Team</div>
                            <div className="text-900 font-medium" style={{ fontSize: '0.7rem' }}>{collab.members?.length || 0}</div>
                        </div>
                    </div>
                    <div className="col-6 p-1">
                        <div className="surface-50 border-round p-1 text-center">
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>Created</div>
                            <div className="text-600" style={{ fontSize: '0.65rem' }}>{formatTimeAgo(collab.created_at)}</div>
                        </div>
                    </div>
                </div>

                {/* Bottom: Action Buttons - Compact */}
                <div className="flex justify-content-between align-items-center pt-1 border-top-1 surface-border">
                    <Button
                        label="View"
                        icon="pi pi-eye"
                        className="p-button-text p-button-sm"
                        style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                        onClick={() => navigate(`/app/collaborations/${collab.id}`)}
                    />
                    <div className="flex gap-1">
                        {isOwner && isActive && (
                            <Button
                                icon="pi pi-check-circle"
                                className="p-button-success p-button-text p-button-sm"
                                style={{ fontSize: '0.65rem', padding: '0.25rem' }}
                                tooltip="Complete"
                                onClick={() => navigate(`/app/collaborations/${collab.id}?action=complete`)}
                            />
                        )}
                        {canLeave && (
                            <Button
                                icon="pi pi-sign-out"
                                className="p-button-warning p-button-text p-button-sm"
                                style={{ fontSize: '0.65rem', padding: '0.25rem' }}
                                tooltip="Leave"
                                onClick={() => handleLeave(collab)}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Mobile card list view with server-side pagination - Compact
    const renderMobileList = (data) => {
        if (data.length === 0) {
            return emptyTemplate();
        }

        return (
            <div className="p-2">
                {/* Mobile Cards with gap */}
                <div className="flex flex-column gap-2">
                    {data.map(collab => renderMobileCard(collab))}
                </div>

                {/* Paginator - server-side pagination - Compact */}
                {totalRecords > ROWS_PER_PAGE && (
                    <Paginator
                        first={first}
                        rows={ROWS_PER_PAGE}
                        totalRecords={totalRecords}
                        onPageChange={onPageChange}
                        className="mt-2 p-paginator-sm"
                        template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{currentPage}/{totalPages}"
                    />
                )}
            </div>
        );
    };

    // Stats configuration
    const statsConfig = [
        { key: 'active', label: 'Active', icon: 'pi-play-circle', bgColor: '#f0fdf4', color: '#22c55e' },
        { key: 'completed', label: 'Completed', icon: 'pi-check-circle', bgColor: '#dbeafe', color: '#3b82f6' },
        { key: 'total_earnings', label: 'Total Earnings', icon: 'pi-indian-rupee', bgColor: '#ede9fe', color: '#8b5cf6', format: 'currency' }
    ];

    // Render DataTable with server-side pagination (no header - filters are above)
    const renderDataTable = (data) => (
        <DataTable
            value={data}
            paginator={totalRecords > ROWS_PER_PAGE}
            first={first}
            rows={ROWS_PER_PAGE}
            totalRecords={totalRecords}
            onPage={onPageChange}
            lazy
            responsiveLayout="scroll"
            stripedRows
            className="p-datatable-sm"
            emptyMessage={emptyTemplate()}
        >
            <Column header="Lead" body={leadInfoTemplate} style={{ minWidth: '240px' }} />
            <Column header="My Role" body={myRoleTemplate} style={{ minWidth: '120px' }} />
            <Column header="Commission Model" body={commissionModelTemplate} style={{ minWidth: '150px' }} />
            <Column header="My Share" body={myCommissionTemplate} style={{ minWidth: '120px' }} />
            <Column header="Team" body={membersTemplate} style={{ minWidth: '160px' }} />
            <Column header="Progress" body={progressTemplate} style={{ minWidth: '100px' }} />
            <Column header="Status" body={statusTemplate} style={{ minWidth: '100px' }} />
            <Column header="Created" body={dateTemplate} style={{ minWidth: '100px' }} />
            <Column header="" body={actionsTemplate} style={{ minWidth: '140px' }} frozen alignFrozen="right" />
        </DataTable>
    );

    return (
        <div className="collaborations-list">
            <NavigationTabMenu tabs={LEAD_DEALS_TABS} />
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Page Header - Compact */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="flex align-items-center justify-content-between">
                    <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>My Deals</h2>
                    <div className="flex align-items-center gap-1">
                        <Button
                            icon="pi pi-shopping-cart"
                            className="p-button-text p-button-sm"
                            style={{ fontSize: '0.7rem', width: '1.75rem', height: '1.75rem' }}
                            onClick={() => navigate('/app/marketplace')}
                            tooltip="Browse Marketplace"
                        />
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-sm"
                            style={{ fontSize: '0.7rem', width: '1.75rem', height: '1.75rem' }}
                            onClick={() => fetchCollaborations()}
                            loading={loading}
                            tooltip="Refresh"
                        />
                    </div>
                </div>
            </div>

            {/* Stats Cards - Compact */}
            <div className="grid mb-2">
                {statsConfig.map((stat) => (
                    <div key={stat.key} className="col-6 md:col-4 p-1">
                        <div
                            className={`surface-card border-round shadow-1 p-3 h-full ${stat.onClick ? 'cursor-pointer hover:shadow-2 transition-all transition-duration-200' : ''}`}
                            onClick={stat.onClick}
                        >
                            <div className="flex align-items-center gap-3">
                                <div
                                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                                    style={{
                                        width: '44px',
                                        height: '44px',
                                        backgroundColor: stat.bgColor
                                    }}
                                >
                                    <i
                                        className={`pi ${stat.icon}`}
                                        style={{ color: stat.color, fontSize: '1.25rem' }}
                                    />
                                </div>
                                <div>
                                    <div
                                        className="font-bold"
                                        style={{ color: stat.color, fontSize: '1.75rem', lineHeight: 1.1 }}
                                    >
                                        {stat.format === 'currency'
                                            ? formatCurrency(stats[stat.key] || 0)
                                            : stats[stat.key] || 0
                                        }
                                    </div>
                                    <div className="text-500" style={{ fontSize: '0.9rem' }}>{stat.label}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters Row - Grid layout for proper alignment */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="grid align-items-end m-0">
                    {/* Search */}
                    <div className="col-6 md:col-3 lg:col-3 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>Search</label>
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search" style={{ fontSize: '0.7rem' }} />
                            <InputText
                                value={globalFilter}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                                placeholder="Name, tracking code..."
                                className="w-full p-inputtext-sm"
                                style={{ fontSize: '0.7rem', padding: '0.5rem 0.5rem 0.5rem 2rem' }}
                            />
                        </span>
                    </div>

                    {/* Loan Type */}
                    <div className="col-6 md:col-3 lg:col-3 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>Loan Type</label>
                        <Dropdown
                            value={loanTypeFilter}
                            options={PRODUCT_TYPE_DETAILED_OPTIONS}
                            onChange={(e) => {
                                setLoanTypeFilter(e.value);
                                setFirst(0);
                                setPage(1);
                            }}
                            placeholder="All Products"
                            optionLabel="label"
                            optionValue="value"
                            showClear
                            filter
                            filterPlaceholder="Search..."
                            className="w-full p-inputtext-sm"
                            panelStyle={{ fontSize: '0.7rem' }}
                            pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem', padding: '0.4rem 0.75rem' } }, filterInput: { style: { fontSize: '0.7rem' } } }}
                        />
                    </div>

                    {/* From Date */}
                    <div className="col-6 md:col-2 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>From Date</label>
                        <Calendar
                            value={dateFrom}
                            onChange={(e) => {
                                setDateFrom(e.value);
                                setFirst(0);
                                setPage(1);
                            }}
                            placeholder="From"
                            className="w-full p-inputtext-sm"
                            showIcon
                            inputStyle={{ fontSize: '0.7rem' }}
                            dateFormat="dd M yy"
                            showButtonBar
                            maxDate={dateTo || undefined}
                            pt={{
                                input: { style: { fontSize: '0.7rem' } },
                                dropdownButton: { style: { width: '2rem', padding: '0' } },
                                icon: { style: { fontSize: '0.75rem' } }
                            }}
                        />
                    </div>

                    {/* To Date */}
                    <div className="col-6 md:col-2 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.86rem' }}>To Date</label>
                        <Calendar
                            value={dateTo}
                            onChange={(e) => {
                                setDateTo(e.value);
                                setFirst(0);
                                setPage(1);
                            }}
                            placeholder="To"
                            className="w-full p-inputtext-sm"
                            showIcon
                            inputStyle={{ fontSize: '0.7rem' }}
                            dateFormat="dd M yy"
                            showButtonBar
                            minDate={dateFrom || undefined}
                            pt={{
                                input: { style: { fontSize: '0.7rem' } },
                                dropdownButton: { style: { width: '2rem', padding: '0' } },
                                icon: { style: { fontSize: '0.75rem' } }
                            }}
                        />
                    </div>

                    {/* View Toggle + Refresh */}
                    <div className="col-6 md:col-2 lg:col-2 p-1 flex align-items-end justify-content-end gap-1">
                        <div className="hidden md:flex align-items-center gap-1">
                            <SelectButton
                                value={viewMode}
                                options={viewOptions}
                                onChange={(e) => e.value && setViewMode(e.value)}
                                optionLabel="icon"
                                itemTemplate={(option) => <i className={option.icon} style={{ fontSize: '0.7rem' }} />}
                                style={{ transform: 'scale(0.85)' }}
                            />
                        </div>
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-rounded"
                            onClick={() => fetchCollaborations()}
                            loading={loading}
                            tooltip="Refresh"
                            tooltipOptions={{ position: 'top' }}
                            style={{ width: '1.75rem', height: '1.75rem' }}
                        />
                    </div>
                </div>

                {/* Active filters & Results count */}
                {(loanTypeFilter || dateFrom || dateTo) && (
                    <div className="flex align-items-center justify-content-between mt-1 pt-1 border-top-1 surface-border">
                        <span className="text-500" style={{ fontSize: '0.7rem' }}>
                            {[loanTypeFilter, dateFrom, dateTo].filter(Boolean).length} filter{[loanTypeFilter, dateFrom, dateTo].filter(Boolean).length > 1 ? 's' : ''} active
                        </span>
                        <Button
                            label="Clear"
                            icon="pi pi-filter-slash"
                            className="p-button-text p-button-sm"
                            onClick={() => {
                                setGlobalFilter('');
                                setLoanTypeFilter(null);
                                setDateFrom(null);
                                setDateTo(null);
                                setFirst(0);
                                setPage(1);
                            }}
                            style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }}
                        />
                    </div>
                )}
            </div>

            {/* Status Filter Chips - Compact */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="flex flex-wrap md:flex-nowrap gap-1">
                    {tabConfig.map((tab, index) => {
                        const isActive = activeTab === index;
                        const bgColor = isActive
                            ? (tab.color ? `var(--${tab.color}-100)` : 'var(--primary-100)')
                            : 'var(--surface-100)';
                        const borderColor = isActive
                            ? (tab.color ? `var(--${tab.color}-300)` : 'var(--primary-300)')
                            : 'var(--surface-200)';
                        const iconColor = tab.color ? `text-${tab.color}` : 'text-primary';

                        return (
                            <div
                                key={tab.key}
                                className="flex align-items-center justify-content-center gap-1 px-2 py-1 border-round cursor-pointer transition-all transition-duration-200"
                                style={{
                                    backgroundColor: bgColor,
                                    border: `1px solid ${borderColor}`,
                                    flex: '1 1 30%',
                                    maxWidth: 'calc(33.33% - 0.25rem)',
                                    fontSize: '0.7rem'
                                }}
                                onClick={() => handleTabChange(index)}
                            >
                                <i className={`pi ${tab.icon} ${iconColor}`} style={{ fontSize: '0.65rem' }} />
                                <span className="text-700 font-medium hidden md:inline">{tab.label}</span>
                                <Badge
                                    value={stats[tab.statsKey] || 0}
                                    severity={tab.color || 'primary'}
                                    style={{ fontSize: '0.6rem', padding: '0 4px', minWidth: '16px', height: '14px', lineHeight: '14px' }}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Content - Grid/List/Mobile Cards */}
            <div className="surface-card border-round shadow-1">
                {/* Desktop: Grid or List View */}
                <div className="hidden md:block p-2">
                    {loading ? (
                        renderSkeleton()
                    ) : collaborations.length === 0 ? (
                        emptyTemplate()
                    ) : viewMode === 'grid' ? (
                        <>
                            <div className="grid">
                                {collaborations.map((collab) => (
                                    <div key={collab.id} className="col-12 md:col-6 lg:col-4 xl:col-3 p-2">
                                        <CollaborationCard collab={collab} />
                                    </div>
                                ))}
                            </div>
                            {/* Paginator for Grid View - Always rendered to prevent CLS */}
                            <div style={{ visibility: totalRecords > ROWS_PER_PAGE ? 'visible' : 'hidden' }}>
                                <Paginator
                                    first={first}
                                    rows={ROWS_PER_PAGE}
                                    totalRecords={totalRecords || 0}
                                    onPageChange={onPageChange}
                                    className="mt-2 p-paginator-sm"
                                    template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{currentPage}/{totalPages}"
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <ListHeader />
                            {collaborations.map((collab) => (
                                <CollaborationRow key={collab.id} collab={collab} />
                            ))}
                            {/* Paginator for List View - Always rendered to prevent CLS */}
                            <div style={{ visibility: totalRecords > ROWS_PER_PAGE ? 'visible' : 'hidden' }}>
                                <Paginator
                                    first={first}
                                    rows={ROWS_PER_PAGE}
                                    totalRecords={totalRecords || 0}
                                    onPageChange={onPageChange}
                                    className="mt-2 p-paginator-sm"
                                    template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{currentPage}/{totalPages}"
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Mobile: Card List */}
                <div className="block md:hidden">
                    {loading ? renderSkeleton() : renderMobileList(collaborations)}
                </div>
            </div>

            {/* Embedded Styles for Compact Design */}
            <style>{`
                /* Paginator compact styling */
                .collaborations-list .p-paginator-sm {
                    padding: 0.25rem;
                }
                .collaborations-list .p-paginator-sm .p-paginator-element {
                    min-width: 1.5rem;
                    height: 1.5rem;
                    font-size: 0.7rem;
                }
                .collaborations-list .p-paginator-sm .p-paginator-current {
                    font-size: 0.7rem;
                    padding: 0 0.5rem;
                }

                /* Filter bar compact styling */
                .collaborations-list .p-calendar .p-inputtext {
                    padding: 0.5rem 0.5rem;
                    font-size: 0.7rem;
                }
                .collaborations-list .p-calendar .p-datepicker-trigger {
                    width: 2rem;
                    padding: 0;
                }
                .collaborations-list .p-calendar .p-datepicker-trigger .p-icon {
                    width: 0.75rem;
                    height: 0.75rem;
                }
                .collaborations-list .p-dropdown .p-dropdown-label {
                    padding: 0.5rem 0.5rem;
                    font-size: 0.7rem;
                }
                .collaborations-list .p-dropdown .p-dropdown-trigger {
                    width: 2rem;
                }
                @media screen and (max-width: 768px) {
                    .collaborations-list .p-calendar,
                    .collaborations-list .p-dropdown {
                        font-size: 0.65rem;
                    }
                }

                /* Mobile card styling */
                .mobile-collab-card {
                    touch-action: manipulation;
                }
                .mobile-collab-card:active {
                    transform: scale(0.99);
                }

                @media (max-width: 576px) {
                    /* Extra compact for very small screens */
                    .collaborations-list .p-paginator-sm .p-paginator-element {
                        min-width: 1.25rem;
                        height: 1.25rem;
                        font-size: 0.65rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default CollaborationsList;
